<script setup lang="ts">
/**
 * Bar for bulk actions on a multi-selection: „3 ausgewählt“, the actions
 * (default slot) and „Auswahl aufheben“. It appears once `minCount` rows are
 * selected and disappears again when the selection is cleared.
 *
 * Bind it to an `NxTable` with row selection by passing the table's template
 * ref as `table` — the bar then reads the count from it and clears the
 * table's selection itself. Without a table, pass `count` and handle `clear`.
 *
 * `placement="floating"` pins it to the bottom centre of the viewport (the
 * mail list's bulk bar); `inline` keeps it in the flow, e.g. above a table;
 * `embedded` sits inside a row of the caller (a list head next to „Alle
 * auswählen“) without a surface, frame or padding of its own.
 *
 * `clearable: false` drops the × when the host clears the selection itself
 * (that „Alle auswählen“); Escape inside the bar still clears.
 *
 * `persistent` keeps the toolbar in the layout at every count. Below
 * `minCount`, its `idle` slot shows the collection's regular actions instead
 * of selection actions, count and clear. One standard control-height row
 * scrolls its actions sideways instead of wrapping, so selecting entries
 * never moves the collection below it. Inline placement shares the workspace
 * header's responsive horizontal gutter.
 */
import { computed, unref, type Ref } from 'vue';
import { DismissIcon } from '@blpde/nx-icons';
import NxIconButton from '../IconButton/NxIconButton.vue';

/**
 * What the bar needs from a table. A template ref to `NxTable` hands its
 * exposed refs over UNWRAPPED (`selectedIds` is then a plain `Set`), the
 * expose object itself carries the ref — both are accepted.
 */
type SelectionSource = {
  selectedIds: Set<string> | Readonly<Ref<Set<string>>>;
  deselectAll: () => void;
};

const props = withDefaults(
  defineProps<{
    /** Number of selected entries. Ignored when `table` is set. */
    count?: number;
    /** An `NxTable` instance (template ref) with row selection. */
    table?: SelectionSource | null;
    /** Show the bar from this many selected entries on. */
    minCount?: number;
    placement?: 'floating' | 'inline' | 'embedded';
    /** Keep a single toolbar row visible; `idle` supplies actions below `minCount`. */
    persistent?: boolean;
    /** Show the × that clears the selection. Defaults to `true`. */
    clearable?: boolean;
    countLabel?: (count: number) => string;
    clearLabel?: string;
    ariaLabel?: string;
  }>(),
  {
    count: 0,
    table: null,
    minCount: 1,
    placement: 'floating',
    persistent: false,
    clearable: true,
    countLabel: (count: number) => `${count} ausgewählt`,
    clearLabel: 'Auswahl aufheben',
    ariaLabel: 'Mehrfachauswahl',
  }
);

const emit = defineEmits<{ clear: [] }>();

defineSlots<{
  default?(props: { count: number }): unknown;
  idle?(props: { count: number }): unknown;
}>();

const selected = computed(() => (props.table ? unref(props.table.selectedIds).size : props.count));
const selectionActive = computed(() => selected.value >= Math.max(1, props.minCount));
const visible = computed(() => props.persistent || selectionActive.value);

function clear() {
  props.table?.deselectAll();
  emit('clear');
}

function onKeydown(event: KeyboardEvent) {
  if (event.defaultPrevented || event.key !== 'Escape' || !selectionActive.value) return;
  event.preventDefault();
  clear();
}
</script>

<template>
  <div
    v-if="visible"
    class="nx-selection-bar"
    :class="[`placement-${placement}`, { 'is-persistent': persistent }]"
    role="toolbar"
    :aria-label="ariaLabel"
    @keydown="onKeydown"
  >
    <span v-if="selectionActive" class="nx-selection-bar__count" aria-live="polite">
      {{ countLabel(selected) }}
    </span>
    <div class="nx-selection-bar__actions">
      <slot v-if="selectionActive" :count="selected" />
      <slot v-else name="idle" :count="selected" />
    </div>
    <NxIconButton
      v-if="selectionActive && clearable"
      height="s"
      data-test="clear"
      :title="clearLabel"
      @click="clear"
    >
      <DismissIcon filled class="nxt-icon-md" />
    </NxIconButton>
  </div>
</template>

<style scoped>
.nx-selection-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  color: var(--nxt-fg);
}
/* Floating: a card over the page, so it gets the frame and the shadow. */
.nx-selection-bar.placement-floating {
  position: fixed;
  bottom: var(--nxt-space-lg);
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100vw - var(--nxt-space-lg));
  background: var(--nxt-surface-raised-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  box-shadow: var(--nxt-shadow-lg);
  z-index: var(--nxt-z-float);
}
/* Inline: a strip across the top of the list it acts on — edge to edge, a
   quiet brand tint and one rule below, no frame of its own. A card with
   corners inside the list column read as a stray box. */
.nx-selection-bar.placement-inline {
  background: color-mix(in srgb, var(--nxt-brand-bg) 6%, var(--nxt-surface-bg));
  border-block-end: var(--nxt-hairline) solid var(--nxt-border);
}
/* Embedded: part of the caller's row, which brings the surface and spacing. */
.nx-selection-bar.placement-embedded {
  padding: 0;
}
.nx-selection-bar__count {
  padding: 0 var(--nxt-space-xs);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-brand-bg);
  white-space: nowrap;
}
.nx-selection-bar__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
  flex: 1;
  min-width: 0;
}
/* A collection toolbar must not change the list's position when its actions
   change. Its standard-height controls stay on one row; the action strip
   scrolls while count and clear stay available. Padding inside that strip
   leaves room for the controls' focus rings without a vertical scrollbar. */
.nx-selection-bar.is-persistent {
  flex-wrap: nowrap;
  min-width: 0;
  padding-block: 0;
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-control);
}
.nx-selection-bar.is-persistent .nx-selection-bar__actions {
  box-sizing: border-box;
  flex-wrap: nowrap;
  block-size: calc(1lh + 4 * var(--nxt-space-xs) + 2 * var(--nxt-hairline));
  padding-block: var(--nxt-space-xs);
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}
.nx-selection-bar.is-persistent .nx-selection-bar__actions > :deep(*) {
  flex-shrink: 0;
  white-space: nowrap;
}
.nx-selection-bar.is-persistent > :not(.nx-selection-bar__actions) {
  flex-shrink: 0;
}
.nx-selection-bar.is-persistent.placement-inline {
  padding-inline: var(--nxt-space-lg);
}
@media (max-width: 48em) {
  .nx-selection-bar.is-persistent.placement-inline {
    padding-inline: var(--nxt-space-sm);
  }
}
</style>
